import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react";
import chevronLeft from "@iconify/icons-eva/chevron-left-outline";
import chevronRight from "@iconify/icons-eva/chevron-right-outline";
import logOutOutline from "@iconify/icons-eva/log-out-outline";
import { styled } from "@mui/system";
import { Box, Drawer, IconButton, Tooltip, Typography } from "@mui/material";
import Logo from "../../components/Logo";
import Scrollbar from "../../components/Scrollbar";
import NavSection from "../../components/NavSection";
import { MHidden } from "../../components/@material-extend";
import changeSideBar from "./SidebarConfig";
import { useJWT } from "../../contexts/auth";

export const DRAWER_WIDTH = 260;
export const DRAWER_WIDTH_COLLAPSED = 84;

const RootStyle = styled("div", {
  shouldForwardProp: (prop) => prop !== "collapsed",
})<{ collapsed?: boolean }>(({ theme, collapsed }) => ({
  [theme.breakpoints.up("lg")]: {
    flexShrink: 0,
    width: collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
    transition: "width 200ms ease",
  },
}));

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
  collapsed: PropTypes.bool,
  onToggleCollapsed: PropTypes.func,
};

export default function DashboardSidebar({
  isOpenSidebar,
  onCloseSidebar,
  collapsed = false,
  onToggleCollapsed,
}: any) {
  const { pathname } = useLocation();
  const [sidebarConfig, SetSidebarConfig] = useState<any>([]);
  const { setToken } = useJWT();

  const token: any = localStorage.getItem("user");
  const user = JSON.parse(token);

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setToken(null);
  };

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    SetSidebarConfig(changeSideBar(token));
  }, [token]);

  const renderContent = (isCollapsed: boolean) => (
    <Scrollbar
      sx={{
        height: "100vh",
        backgroundColor: "#0B3A5B",
        overflowX: "hidden",
        "& .simplebar-content": {
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflowX: "hidden",
        },
      }}
    >
      <Box
        sx={{
          px: isCollapsed ? 1 : 2,
          pt: 2.5,
          pb: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: isCollapsed ? "center" : "space-between",
        }}
      >
        <Box
          component={RouterLink}
          to="/dashboard/app"
          sx={{ display: "inline-flex", overflow: "hidden" }}
        >
          <Logo sx={{ width: isCollapsed ? 36 : 168, height: isCollapsed ? 36 : "auto" }} />
        </Box>
        {onToggleCollapsed && !isCollapsed && (
          <Tooltip title="Retrair menu" placement="right">
            <IconButton
              onClick={onToggleCollapsed}
              size="small"
              sx={{ color: "rgba(255,255,255,0.8)", "&:hover": { bgcolor: "rgba(255,255,255,0.08)" } }}
            >
              <Icon icon={chevronLeft} width={20} height={20} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {onToggleCollapsed && isCollapsed && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
          <Tooltip title="Expandir menu" placement="right">
            <IconButton
              onClick={onToggleCollapsed}
              size="small"
              sx={{ color: "rgba(255,255,255,0.8)", "&:hover": { bgcolor: "rgba(255,255,255,0.08)" } }}
            >
              <Icon icon={chevronRight} width={20} height={20} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {!isCollapsed && (
        <Box sx={{ px: 3, pb: 2 }}>
          <Typography
            noWrap
            sx={{ color: "rgba(255,255,255,0.72)", fontSize: 12, textAlign: "left" }}
          >
            {user?.email}
          </Typography>
          <Typography
            sx={{
              color: "#fff",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textAlign: "left",
              mt: 0.25,
            }}
          >
            {user?.role?.toUpperCase()}
          </Typography>
        </Box>
      )}

      <NavSection navConfig={sidebarConfig} collapsed={isCollapsed} />

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ p: isCollapsed ? 1 : 2, pb: 2 }}>
        <Tooltip title={isCollapsed ? "Sair do sistema" : ""} placement="right">
          <Box
            onClick={logout}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: isCollapsed ? "center" : "center",
              gap: 1,
              height: 40,
              borderRadius: "10px",
              cursor: "pointer",
              color: "#fff",
              bgcolor: "#E53935",
              fontSize: 13,
              fontWeight: 700,
              "&:hover": { bgcolor: "#D32F2F" },
            }}
          >
            <Icon icon={logOutOutline} width={18} height={18} />
            {!isCollapsed && "Sair"}
          </Box>
        </Tooltip>
        {!isCollapsed && (
          <Typography
            sx={{
              color: "rgba(255,255,255,0.45)",
              fontSize: 10,
              textAlign: "center",
              mt: 1.5,
            }}
          >
            Optools · Braint Tech
          </Typography>
        )}
      </Box>
    </Scrollbar>
  );

  return (
    <RootStyle collapsed={collapsed}>
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH, border: "none" },
          }}
        >
          {renderContent(false)}
        </Drawer>
      </MHidden>

      <MHidden width="lgDown">
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: {
              width: collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
              border: "none",
              bgcolor: "#0B3A5B",
              transition: "width 200ms ease",
              overflowX: "hidden",
            },
          }}
        >
          {renderContent(collapsed)}
        </Drawer>
      </MHidden>
    </RootStyle>
  );
}
